import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Loader2,
  CheckCircle,
  Star,
  Crown,
  Zap,
  Mail,
  ArrowLeft,
  AlertCircle,
  Building2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import api from '@/services/api';
import { logError, logDebug } from '@/utils/errorHandler';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from 'react-i18next';

interface PlanInfo {
  name: string;
  price: number;
  billing: 'monthly' | 'annual';
  features: string[];
}

interface FormData {
  orgName: string;
  adminEmail: string;
  selectedPlan: string;
  termsAccepted: boolean;
}

const RequestOrganizationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  // Get plan from URL params, handle legacy 'professional' param
  const rawPlanParam = searchParams.get('plan') || 'basic';
  const planParam =
    rawPlanParam === 'professional' || rawPlanParam === 'pro' ? 'basic' : rawPlanParam;
  const billingParam = searchParams.get('billing') || 'monthly';

  const { getPlanName } = useMarketingTranslations();

  // State management
  const [submitting, setSubmitting] = useState(false);
  const [success] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(
    billingParam as 'monthly' | 'annual'
  );

  const [formData, setFormData] = useState<FormData>({
    orgName: '',
    adminEmail: '',
    selectedPlan: planParam,
    termsAccepted: false,
  });

  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});

  // Plan configurations with updated pricing
  const planConfigs: Record<string, PlanInfo> = {
    basic: {
      name: getPlanName('basic'),
      price: billingParam === 'annual' ? 42 : 49,
      billing: billingParam as 'monthly' | 'annual',
      features: [
        t('features.upTo2000SKUs'),
        t('features.storage30GB'),
        t('features.users5'),
        t('features.connectors2'),
      ],
    },
    business: {
      name: getPlanName('business'),
      price: billingParam === 'annual' ? 628 : 739,
      billing: billingParam as 'monthly' | 'annual',
      features: [
        t('features.upTo10000SKUs'),
        t('features.storage100GB'),
        t('features.users15'),
        t('features.connectors8'),
        t('features.advancedAnalytics'),
      ],
    },
    enterprise: {
      name: getPlanName('enterprise'),
      price: billingParam === 'annual' ? 1250 : 1470,
      billing: billingParam as 'monthly' | 'annual',
      features: [
        t('features.upTo100000SKUs'),
        t('features.storage500GB'),
        t('features.usersUnlimited'),
        t('features.connectorsUnlimited'),
        t('features.customIntegrations'),
        t('features.dedicatedAccountManager'),
      ],
    },
  };

  // Update selected plan info to use current billing cycle
  const selectedPlanInfo = (() => {
    const baseConfig = planConfigs[formData.selectedPlan] || planConfigs.basic;
    return {
      ...baseConfig,
      price:
        billingCycle === 'annual'
          ? planConfigs[formData.selectedPlan]?.price || planConfigs.basic.price
          : formData.selectedPlan === 'basic'
            ? 49
            : formData.selectedPlan === 'business'
              ? 739
              : 1470,
      billing: billingCycle,
    };
  })();

  // Form validation
  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};

    if (!formData.orgName.trim()) {
      errors.orgName = t('validation.orgNameRequired');
    } else if (formData.orgName.trim().length < 2) {
      errors.orgName = t('validation.orgNameMinLength');
    }

    if (!formData.adminEmail.trim()) {
      errors.adminEmail = t('validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      errors.adminEmail = t('validation.emailInvalid');
    }

    if (!formData.termsAccepted) {
      errors.termsAccepted = true;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission - now integrates with Stripe
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('validation.fixErrors'));
      return;
    }

    try {
      logDebug('Organization request form submission started', {
        component: 'RequestOrganizationPage',
        action: 'submitForm',
        metadata: {
          submitting,
          success,
          mounted,
          selectedPlan: formData.selectedPlan,
          billingCycle,
        },
      });

      const submissionData = {
        email: formData.adminEmail.trim(),
        org_name: formData.orgName.trim(),
        plan: formData.selectedPlan,
        billing_cycle: billingCycle,
      };

      logDebug('Submitting organization request to Stripe', {
        component: 'RequestOrganizationPage',
        action: 'createCheckoutSession',
        metadata: {
          orgName: submissionData.org_name,
          plan: submissionData.plan,
          billingCycle: submissionData.billing_cycle,
          email: submissionData.email,
        },
      });

      setSubmitting(true);
      setError(null);

      // Create Stripe checkout session
      const response = await api.post(
        '/api/organizations/stripe/create-checkout-session/',
        submissionData
      );

      if (response.data?.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(t('errors.noCheckoutUrl'));
      }
    } catch (err: any) {
      logError(
        'Failed to create Stripe checkout session',
        {
          component: 'RequestOrganizationPage',
          action: 'createCheckoutSession',
          metadata: {
            orgName: formData.orgName,
            plan: formData.selectedPlan,
            billingCycle,
            email: formData.adminEmail,
            error: err,
          },
        },
        {
          showToast: false, // We handle toast manually below
          userFacing: true,
        }
      );

      if (mounted) {
        let errorMessage = t('errors.paymentFailed');

        if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }

        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Update URL parameters
  const updateUrlParams = (plan: string, billing: 'monthly' | 'annual') => {
    const newParams = new URLSearchParams();
    newParams.set('plan', plan);
    newParams.set('billing', billing);
    setSearchParams(newParams, { replace: true });
  };

  // Handle input changes
  const handleInputChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const newValue = e.target.value;

      setFormData(prev => ({
        ...prev,
        [field]: newValue,
      }));

      // Update URL when plan changes
      if (field === 'selectedPlan') {
        updateUrlParams(newValue, billingCycle);
      }

      // Clear error for this field when user starts typing
      if (formErrors[field]) {
        setFormErrors(prev => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  // Get plan icon
  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'basic':
        return <Building2 className="size-5" />;
      case 'professional':
        return <Star className="size-5" />;
      case 'enterprise':
        return <Crown className="size-5" />;
      default:
        return <Zap className="size-5" />;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, []);

  // If successful submission
  if (success) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
            <CardHeader className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-500/10"
              >
                <CheckCircle className="size-8 text-green-400" />
              </motion.div>
              <CardTitle className="text-2xl font-bold text-white">{t('success.title')}</CardTitle>
              <CardDescription className="text-slate-300">{t('success.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <div className="space-y-2 text-sm text-slate-400">
                <p>
                  {t('success.message1')} <strong className="text-white">{formData.orgName}</strong>{' '}
                  {t('success.message2')} {selectedPlanInfo.name}.
                </p>
                <p>
                  {t('success.message3')}{' '}
                  <strong className="text-white">{formData.adminEmail}</strong>{' '}
                  {t('success.message4')}
                </p>
              </div>
              <div className="space-y-3 pt-4">
                <Button
                  asChild
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600"
                >
                  <Link to="/marketing/pricing">{t('success.backToPricing')}</Link>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700/50"
                >
                  <Link to="/">{t('success.returnToHome')}</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary-600 p-2">
                <img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt="KernLogic"
                  className="size-6 text-white"
                />
              </div>
              <span className="text-xl font-bold text-white">KernLogic</span>
            </Link>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-slate-600 bg-slate-800/80 text-slate-300 hover:border-slate-500 hover:bg-slate-700/80 hover:text-white"
              >
                <Link to="/marketing/pricing">
                  <ArrowLeft className="mr-2 size-4" />
                  {t('navigation.backToPricing')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="mb-4 text-4xl font-bold text-white">{t('title')}</h1>
            <p className="text-xl text-slate-300">{t('subtitle')}</p>
          </motion.div>

          <div className="grid gap-12 lg:grid-cols-2">
            {/* Selected Plan Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="border-primary-500/50 bg-slate-800/90 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="rounded-lg bg-primary-500/10 p-2 text-primary-400">
                      {getPlanIcon(formData.selectedPlan)}
                    </div>
                    <div>
                      <CardTitle className="text-white">{selectedPlanInfo.name}</CardTitle>
                      <CardDescription className="text-slate-300">
                        €{selectedPlanInfo.price}
                        {t('plan.perMonth')}
                        {selectedPlanInfo.billing === 'annual'
                          ? ` ${t('plan.billedAnnually')}`
                          : ''}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-white">{t('plan.includedFeatures')}</h4>
                    <ul className="space-y-2">
                      {selectedPlanInfo.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-slate-300">
                          <CheckCircle className="mr-2 size-4 shrink-0 text-green-400" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Billing Cycle Toggle */}
                    <div className="pt-4">
                      <Label className="text-white">{t('plan.billingCycle')}</Label>
                      <div className="mt-2 flex rounded-lg border border-slate-600 bg-slate-700 p-1">
                        <button
                          type="button"
                          onClick={() => {
                            setBillingCycle('monthly');
                            updateUrlParams(formData.selectedPlan, 'monthly');
                          }}
                          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                            billingCycle === 'monthly'
                              ? 'bg-primary-600 text-white'
                              : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          {t('plan.monthly')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setBillingCycle('annual');
                            updateUrlParams(formData.selectedPlan, 'annual');
                          }}
                          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                            billingCycle === 'annual'
                              ? 'bg-primary-600 text-white'
                              : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          {t('plan.annual')}{' '}
                          <span className="text-xs text-green-400">{t('plan.annualDiscount')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Plan Selector */}
                    <div className="pt-4">
                      <Label htmlFor="selectedPlan" className="text-white">
                        {t('plan.changePlan')}
                      </Label>
                      <select
                        id="selectedPlan"
                        value={formData.selectedPlan}
                        onChange={handleInputChange('selectedPlan')}
                        className="mt-1 w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="basic">
                          {getPlanName('basic')} - €{billingCycle === 'annual' ? 42 : 49}
                          {t('plan.perMonth')}
                        </option>
                        <option value="business">
                          {getPlanName('business')} - €{billingCycle === 'annual' ? 628 : 739}
                          {t('plan.perMonth')}
                        </option>
                        <option value="enterprise">
                          {getPlanName('enterprise')} - €{billingCycle === 'annual' ? 1250 : 1470}
                          {t('plan.perMonth')}
                        </option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Request Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white">{t('form.organizationDetails')}</CardTitle>
                  <CardDescription className="text-slate-300">
                    {t('form.organizationDetailsDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {error && (
                    <Alert className="mb-6 border-red-500/50 bg-red-500/10">
                      <AlertCircle className="size-4" />
                      <AlertDescription className="text-red-400">{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Organization Name */}
                    <div>
                      <Label htmlFor="orgName" className="text-white">
                        {t('form.organizationName')} *
                      </Label>
                      <Input
                        id="orgName"
                        type="text"
                        value={formData.orgName}
                        onChange={handleInputChange('orgName')}
                        className={`mt-1 border-slate-600 bg-slate-700 text-white placeholder:text-slate-400 focus:border-primary-500 ${
                          formErrors.orgName ? 'border-red-500' : ''
                        }`}
                        placeholder={t('form.organizationNamePlaceholder')}
                        disabled={submitting}
                      />
                      {formErrors.orgName && (
                        <p className="mt-1 text-sm text-red-400">{formErrors.orgName}</p>
                      )}
                    </div>

                    {/* Admin Email */}
                    <div>
                      <Label htmlFor="adminEmail" className="text-white">
                        {t('form.adminEmail')} *
                      </Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        value={formData.adminEmail}
                        onChange={handleInputChange('adminEmail')}
                        className={`mt-1 border-slate-600 bg-slate-700 text-white placeholder:text-slate-400 focus:border-primary-500 ${
                          formErrors.adminEmail ? 'border-red-500' : ''
                        }`}
                        placeholder={t('form.adminEmailPlaceholder')}
                        disabled={submitting}
                      />
                      {formErrors.adminEmail && (
                        <p className="mt-1 text-sm text-red-400">{formErrors.adminEmail}</p>
                      )}
                      <p className="mt-1 text-xs text-slate-400">{t('form.adminEmailDesc')}</p>
                    </div>

                    {/* Terms and Privacy Policy Acceptance */}
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="termsAccepted"
                          checked={formData.termsAccepted}
                          onCheckedChange={checked =>
                            setFormData(prev => ({ ...prev, termsAccepted: !!checked }))
                          }
                          className={`mt-1 ${formErrors.termsAccepted ? 'border-red-500' : ''}`}
                          disabled={submitting}
                        />
                        <Label
                          htmlFor="termsAccepted"
                          className="cursor-pointer text-sm leading-relaxed text-slate-300"
                        >
                          {t('checkbox.agreeToTerms')}{' '}
                          <Link
                            to="/terms"
                            className="text-primary-400 underline hover:text-primary-300"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('checkbox.termsOfService')}
                          </Link>{' '}
                          {t('checkbox.and')}{' '}
                          <Link
                            to="/privacy"
                            className="text-primary-400 underline hover:text-primary-300"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('checkbox.privacyPolicy')}
                          </Link>
                        </Label>
                      </div>
                      {formErrors.termsAccepted && (
                        <p className="ml-6 text-sm text-red-400">{t('checkbox.mustAccept')}</p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-primary-600 to-primary-500 py-3 font-semibold text-white hover:from-primary-700 hover:to-primary-600"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          {t('form.submittingRequest')}
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 size-4" />
                          {t('form.submitRequest')}
                        </>
                      )}
                    </Button>

                    <div className="text-center">
                      <p className="text-xs text-slate-400">{t('form.submissionNote')}</p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RequestOrganizationPage;
